/**
 * sofaworx-card-swatches
 *
 * Fabric tile strip for a product card. Selecting a tile swaps the card image
 * and repoints the card's links at that variant. The price is deliberately NOT
 * swapped — cards always show the product's lowest ("From") price regardless of
 * which fabric is selected.
 *
 * Contract — the host card must provide these hooks:
 *   [data-product-card]  ancestor element that owns the card
 *   [data-swatch]        each tile button, carrying data-variant-url and
 *                        optionally data-image / data-srcset
 *   [data-card-img]      the <img> to swap
 *   [data-card-link]     every <a> that should follow the selected variant
 *
 * Shared by sections/sofaworx-collection.liquid and
 * sections/sofaworx-product-carousel.liquid. It lives in assets/ rather than a
 * section's scoped javascript block, because a section-scoped bundle is only
 * loaded when that section is on the page and both sections need this.
 */
(() => {
class SofaworxCardSwatches extends HTMLElement {
  connectedCallback() {
    this.card = this.closest('[data-product-card]');
    if (!this.card) return;
    this.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-swatch]');
      if (swatch) this.select(swatch);
    });
    const base = this.card.querySelector('sofaworx-base-variants');
    if (base) {
      customElements.whenDefined('sofaworx-base-variants').then(() => base.prepare()).then(() => this.collapseIfTilesIdentical());
    } else {
      this.collapseIfTilesIdentical();
    }
  }

  /* The Mira sync can upload the SAME photo once per variant as separate
     Shopify files (distinct image ids/urls, identical pixels), so Liquid
     cannot tell a junk strip of duplicate thumbnails from a real fabric
     strip. Fetch each unique tile thumbnail (same-origin CDN, tiny,
     cache-friendly) and hide the strip when every tile is byte-identical. */
  async collapseIfTilesIdentical() {
    const tiles = this.querySelectorAll('[data-swatch]');
    const images = this.querySelectorAll('[data-swatch] img');
    // Fallback (no-image) tiles differentiate visually — leave those strips alone.
    if (tiles.length < 2 || images.length !== tiles.length) return;

    const urls = [...new Set([...images].map((img) => img.getAttribute('src')))];
    if (urls.some((url) => !url)) return;

    if (urls.length > 1) {
      if (!window.crypto?.subtle) return;
      try {
        const digests = await Promise.all(
          urls.map(async (url) => {
            const bytes = await (await fetch(url, { cache: 'force-cache' })).arrayBuffer();
            const hash = await crypto.subtle.digest('SHA-1', bytes);
            return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, '0')).join('');
          })
        );
        if (new Set(digests).size > 1) return;
      } catch {
        return; // leave the strip visible if a tile can't be fetched
      }
    }

    // Inline style so it beats the class's display: flex.
    this.style.display = 'none';
  }

  select(swatch) {
    this.querySelectorAll('[data-swatch]').forEach((button) => {
      button.setAttribute('aria-pressed', button === swatch ? 'true' : 'false');
    });

    const image = this.card.querySelector('[data-card-img]');
    if (image && swatch.dataset.image) {
      image.src = swatch.dataset.image;
      if (swatch.dataset.srcset) {
        image.srcset = swatch.dataset.srcset;
      } else {
        image.removeAttribute('srcset');
      }
    }

    // Price is intentionally NOT updated — cards always show the product's
    // lowest ("From") price regardless of which fabric is selected.

    if (swatch.dataset.variantUrl) {
      this.card.querySelectorAll('[data-card-link]').forEach((link) => {
        link.href = swatch.dataset.variantUrl;
      });
    }
  }
}
if (!customElements.get('sofaworx-card-swatches')) {
  customElements.define('sofaworx-card-swatches', SofaworxCardSwatches);
}})();
